"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { captureAttribution } from "@/lib/attribution";

const CONSENT_KEY = "hf_cookie_consent";
type ConsentValue = "accepted" | "rejected";

function updateGoogleConsent(granted: boolean) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const value = granted ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

function persistConsent(value: ConsentValue) {
  try { localStorage.setItem(CONSENT_KEY, value); } catch { /* The cookie still persists the explicit choice. */ }
  document.cookie = `${CONSENT_KEY}=${value}; Path=/; Max-Age=${60 * 60 * 24 * 180}; SameSite=Lax`;
}

function hasExplicitChoice(): boolean {
  const explicit = (value: string | null | undefined) => value === "accepted" || value === "rejected";
  try { if (explicit(localStorage.getItem(CONSENT_KEY))) return true; } catch { /* storage kapali olabilir */ }
  const cookie = document.cookie.split("; ").find((part) => part.startsWith(`${CONSENT_KEY}=`));
  return explicit(cookie?.split("=")[1]);
}

/**
 * Bant kararini tarayicida verir: sunucu cerez okumaz ki sayfalar onbelleklenebilsin.
 * Bant sabit konumlu bir katmandir; sonradan acilmasi duzen kaymasi (CLS) uretmez.
 */
export function CookieConsentBanner() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hasExplicitChoice()) setVisible(true);
  }, []);

  function accept() {
    persistConsent("accepted");
    updateGoogleConsent(true);
    (window as Window & { __hfLoadAnalytics?: () => void }).__hfLoadAnalytics?.();
    captureAttribution();
    window.dispatchEvent(new Event("measurement-consent"));
    setVisible(false);
  }

  function reject() {
    persistConsent("rejected");
    updateGoogleConsent(false);
    window.dispatchEvent(new Event("measurement-consent"));
    setVisible(false);
  }

  if (!visible || /(?:^|\/)(?:ad-preview|reklam-onizleme)(?:\/|$)/.test(pathname ?? "")) return null;

  return (
    <div
      role="region"
      aria-label="Çerez tercihleri"
      className="fixed inset-x-3 bottom-3 z-[300] mx-auto max-w-4xl rounded-lg border border-(--color-border) bg-(--color-surface) p-4 shadow-2xl sm:inset-x-6 sm:bottom-6"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 text-[13px] leading-5 text-(--color-muted)">
          <strong className="text-(--color-foreground)">Çerez tercihi:</strong>{" "}
          Analytics ve reklam ölçüm çerezleri için onayınızı alıyoruz. Zorunlu çerezler hizmetin çalışması için kullanılır.
          <Link href="/gizlilik-politikasi" className="ml-1 font-semibold text-(--color-brand) hover:underline">
            Detaylar
          </Link>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={reject}
            className="h-10 rounded-lg border border-(--color-border) px-4 text-[13px] font-semibold text-(--color-foreground) hover:bg-(--color-bg-alt)"
          >
            Reddet
          </button>
          <button
            type="button"
            onClick={accept}
            className="h-10 rounded-lg bg-(--color-brand) px-4 text-[13px] font-bold text-(--color-brand-fg) hover:bg-(--color-brand-dark)"
          >
            Kabul Et
          </button>
        </div>
      </div>
    </div>
  );
}
